import { readable, writable } from "svelte/store"
import { Timeout } from "@utils/helpers/timeout"

let hotKeyMap: { key: string; callback: (down: boolean, e?: KeyboardEvent) => void }[] = []

function isFocussedOnSomethingImportant(t: KeyboardEvent["target"]) {
    return (
        t instanceof HTMLElement &&
        (t.tagName == "INPUT" || t.tagName == "TEXTAREA" || t.isContentEditable)
    )
}

export const altKeyDown = writable(false)

const keyDown = (e: KeyboardEvent) => {
    altKeyDown.set(e.altKey)
    if (isFocussedOnSomethingImportant(e.target)) return
    for (const { key, callback } of hotKeyMap) {
        if (e.key == key) callback(true, e)
    }
}

const keyUp = (e: KeyboardEvent) => {
    altKeyDown.set(e.altKey)
    if (isFocussedOnSomethingImportant(e.target)) return
    for (const { key, callback } of hotKeyMap) {
        if (e.key == key) callback(false, e)
    }
}

window.addEventListener("keydown", keyDown)
window.addEventListener("keyup", keyUp)

/**
 * Registers a hot key for use and allows you to supply a callback function
 * @param key The key to listen to
 * @param callback Returns true when key is pressed and false when key is not
 * @returns destroy function
 */
export function registerHotKey(
    key: KeyboardEvent["key"],
    callback: (down: boolean, e?: KeyboardEvent) => void,
) {
    const hotKey = { key, callback }
    hotKeyMap.push(hotKey)
    return () => {
        const newMap = hotKeyMap.filter((h) => h != hotKey)
        hotKeyMap = newMap
    }
}

/**
 * Pass a callback to be called whenever it changes
 * @param key key to listen to
 * @param callback called every time the key is pressed
 * @param onDown whether to call the key on the down stroke or on the up stroke
 * @returns destroy function to unlisten
 */
export function registerHotKeyTap(key: KeyboardEvent["key"], callback: () => void, onDown = false) {
    const rtn = (down: boolean) => {
        if (down == onDown) callback()
    }
    return registerHotKey(key, rtn)
}

/**
 * Pass a callback to be called whenever it changes
 * @param key key to listen to
 * @param callback called every time the key is pressed
 * @param onDown whether to call the key on the down stroke or on the up stroke
 * @returns destroy function to unlisten
 */
export function registerHotKeyDoubleTap(key: KeyboardEvent["key"], callback: () => void) {
    const timeout = new Timeout(500)
    let tappedOnce = false
    function processTap(down: boolean) {
        // Return if down, only processing up
        if (!down) return

        if (!tappedOnce) {
            tappedOnce = true
            timeout.overwrite(() => (tappedOnce = false))
        } else {
            tappedOnce = false
            timeout.cancel()
            callback()
        }
    }

    registerHotKey(key, processTap)
}

/**
 * Generates a store that toggles true or false every time client hits a key
 * @param key key to listen to
 * @param onDown whether to catch the down stroke or the up stroke
 * @returns readable store
 */
export function generateHotKeyTapStore(key: KeyboardEvent["key"], onDown = false) {
    let on = false
    return readable(on, (set) => {
        registerHotKeyTap(
            key,
            () => {
                on = !on
                set(on)
            },
            onDown,
        )
    })
}

/**
 * Generates a store that toggles true when key goes down and false when key goes up
 * @param key key to listen to
 * @returns readable store
 */
export function generateHotKeyStore(key: KeyboardEvent["key"]) {
    return readable(false, (set) => {
        const callback = (down: boolean) => set(down)
        registerHotKey(key, callback)
    })
}

export const altKeyPressed = writable(false)

export const ctrlCmdKeyPressed = writable(false)

window.addEventListener("keydown", (event) => {
    if (event.altKey) altKeyPressed.set(true)
    if (event.ctrlKey || event.metaKey) ctrlCmdKeyPressed.set(true)
})

window.addEventListener("keyup", (event) => {
    if (!event.altKey) altKeyPressed.set(false)
    if (!(event.ctrlKey && event.metaKey)) ctrlCmdKeyPressed.set(false)
})
